// Create a logical group: slug (auto-derived) + label + kind. Mirrors the
// other create forms (slug latch via slugTouched).

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { logicalGroups } from "../../api/endpoints/logicalGroups";
import { qk } from "../../api/queryKeys";
import { FormShell } from "../../components/forms/FormShell";
import { SelectField } from "../../components/forms/SelectField";
import { TextField } from "../../components/forms/TextField";
import { slugify } from "../../lib/slug";
import type {
  LogicalGroupCreate,
  LogicalGroupKind,
} from "../../api/types/logicalGroup";

interface Props {
  accountSlug: string;
  tenantSlug: string;
  siteSlug: string;
  onDone: () => void;
  onPendingChange?: (pending: boolean) => void;
}

const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;

const KINDS: { value: LogicalGroupKind; label: string }[] = [
  { value: "hvac_zone", label: "HVAC zone" },
  { value: "lighting_scene", label: "Lighting scene" },
  { value: "scene", label: "Scene" },
  { value: "automation_group", label: "Automation group" },
];

export function LogicalGroupCreateForm({
  accountSlug,
  tenantSlug,
  siteSlug,
  onDone,
  onPendingChange,
}: Props) {
  const queryClient = useQueryClient();
  const [slug, setSlug] = useState("");
  const [label, setLabel] = useState("");
  const [kind, setKind] = useState<LogicalGroupKind>("hvac_zone");
  const slugTouched = useRef(false);

  const slugError = useMemo(() => {
    if (!slug) return null;
    return SLUG_RE.test(slug)
      ? null
      : "Slug must match a-z, 0-9, hyphen; 1–63 chars; cannot start with hyphen.";
  }, [slug]);

  const mutation = useMutation({
    mutationFn: () => {
      const body: LogicalGroupCreate = { slug, label, kind };
      return logicalGroups.create(accountSlug, tenantSlug, siteSlug, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.logicalGroups(accountSlug, tenantSlug, siteSlug),
      });
      slugTouched.current = false;
      onDone();
    },
  });

  useEffect(() => {
    onPendingChange?.(mutation.isPending);
  }, [mutation.isPending, onPendingChange]);

  const isValid = slug !== "" && SLUG_RE.test(slug) && label !== "";

  return (
    <FormShell
      submitLabel="Create group"
      onSubmit={(e) => {
        e.preventDefault();
        if (isValid) mutation.mutate();
      }}
      onCancel={onDone}
      pending={mutation.isPending}
      error={mutation.error}
      errorTitle="Create group failed"
    >
      <TextField
        label="Label"
        value={label}
        onChange={(v) => {
          setLabel(v);
          if (!slugTouched.current) setSlug(slugify(v));
        }}
        required
        placeholder="Ground Floor HVAC"
        helper="Human-readable. Slug auto-derives until you edit it."
      />
      <TextField
        label="Slug"
        value={slug}
        onChange={(v) => {
          slugTouched.current = true;
          setSlug(v);
        }}
        required
        placeholder="ground-hvac"
        error={slugError}
      />
      <SelectField
        label="Kind"
        value={kind}
        onChange={(v) => setKind(v as LogicalGroupKind)}
        options={KINDS}
        required
      />
    </FormShell>
  );
}
